import React, { useState, useEffect } from 'react';
import { CalendarDays, Plus, Clock, Users, Loader2, Trash2, ArrowRight, Sun, Moon, Coffee } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { normalizeAppError } from '../../utils/apiErrors';
import { API } from '../../config/apiBase';


export default function EmpresaEscalasPage() {
    const [jornadas, setJornadas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ nome: '', entrada: '08:00', intervalo_inicio: '12:00', intervalo_fim: '13:00', saida: '17:00', dias: ['seg','ter','qua','qui','sex'] });
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        fetch(`${API}/ponto/jornadas`, { headers }).then(r => r.json())
            .then(data => { setJornadas(Array.isArray(data) ? data : []); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const r = await fetch(`${API}/ponto/jornadas`, { method: 'POST', headers, body: JSON.stringify(form) });
            const data = await r.json();
            if (!r.ok) throw new Error(data.detail || 'Erro');
            toast.success('Jornada criada!');
            setJornadas(prev => [...prev, data]);
            setShowForm(false);
            setForm({ nome: '', entrada: '08:00', intervalo_inicio: '12:00', intervalo_fim: '13:00', saida: '17:00', dias: ['seg','ter','qua','qui','sex'] });
        } catch (err) { toast.error(normalizeAppError(err).message); }
        setSaving(false);
    };

    const diasLabels = { seg: 'Seg', ter: 'Ter', qua: 'Qua', qui: 'Qui', sex: 'Sex', sab: 'Sab', dom: 'Dom' };
    const allDias = ['seg','ter','qua','qui','sex','sab','dom'];

    const toggleDia = (d) => {
        setForm(prev => ({ ...prev, dias: prev.dias.includes(d) ? prev.dias.filter(x => x !== d) : [...prev.dias, d] }));
    };

    const calcHours = (j) => {
        const toMin = (t) => { const [h, m] = (t || '00:00').split(':').map(Number); return h * 60 + m; };
        const work = toMin(j.saida || j['saída']) - toMin(j.entrada) - (toMin(j.intervalo_fim) - toMin(j.intervalo_inicio));
        return (work / 60).toFixed(1);
    };

    const totalColabs = jornadas.reduce((a, j) => a + (j.colaboradores_count || 0), 0);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    return (
        <div className="space-y-5" data-testid="empresa-escalas">
            {/* KPI Summary */}
            <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-1">
                        <CalendarDays size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                    </div>
                    <p className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{jornadas.length}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Jornadas ativas</p>
                </div>
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-1">
                        <Users size={14} style={{ color: '#3B82F6' }} />
                    </div>
                    <p className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{totalColabs}</p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Colaboradores alocados</p>
                </div>
                <div className="rounded-xl p-3.5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-1">
                        <Clock size={14} style={{ color: '#10B981' }} />
                    </div>
                    <p className="text-xl font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                        {jornadas.length > 0 ? `${calcHours(jornadas[0])}h` : '0h'}
                    </p>
                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Carga horaria media</p>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: 'var(--muted)' }}>{jornadas.length} jornada(s) cadastrada(s)</p>
                <Button onClick={() => setShowForm(!showForm)} className="btn-gold text-sm" data-testid="btn-nova-jornada">
                    <Plus size={14} className="mr-1" /> Nova Jornada
                </Button>
            </div>

            {/* Create Form */}
            {showForm && (
                <form onSubmit={handleCreate} className="rounded-xl p-4 sm:p-5 space-y-3" style={{ background: 'var(--surface)', border: '1px solid rgba(var(--sv-accent-rgb), 0.3)' }} data-testid="jornada-form">
                    <h4 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Nova Jornada</h4>
                    <input placeholder="Nome (ex: Comercial, Administrativo)" value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} required
                        className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="input-nome-jornada" />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                            { key: 'entrada', label: 'Entrada' },
                            { key: 'intervalo_inicio', label: 'Intervalo In.' },
                            { key: 'intervalo_fim', label: 'Intervalo Fim' },
                            { key: 'saida', label: 'Saída' },
                        ].map((f) => (
                            <div key={f.key}>
                                <label className="text-[10px] block mb-1" style={{ color: 'var(--muted)' }}>{f.label}</label>
                                <input type="time" value={form[f.key]} onChange={e => setForm({...form, [f.key]: e.target.value})}
                                    className="w-full px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                            </div>
                        ))}
                    </div>
                    <div>
                        <label className="text-[10px] block mb-2" style={{ color: 'var(--muted)' }}>Dias da Semana</label>
                        <div className="flex gap-1.5 flex-wrap">
                            {allDias.map((d) => (
                                <button key={d} type="button" onClick={() => toggleDia(d)}
                                    className="w-9 h-9 rounded-lg text-[10px] font-medium transition-all"
                                    style={{ background: form.dias.includes(d) ? 'rgba(var(--sv-accent-rgb), 0.15)' : 'var(--surface-2)', border: `1px solid ${form.dias.includes(d) ? 'var(--sv-accent)' : 'var(--border)'}`, color: form.dias.includes(d) ? 'var(--sv-accent)' : 'var(--muted)' }}>
                                    {diasLabels[d]}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button type="submit" disabled={saving} className="btn-gold text-sm" data-testid="submit-jornada">
                            {saving ? <Loader2 size={14} className="animate-spin" /> : 'Criar Jornada'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="text-sm">Cancelar</Button>
                    </div>
                </form>
            )}

            {/* Jornadas List */}
            {jornadas.length === 0 ? (
                <div className="text-center py-14 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)' }}>
                        <CalendarDays size={24} style={{ color: 'var(--sv-gold-primary)', opacity: 0.6 }} />
                    </div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhuma jornada cadastrada</p>
                    <p className="text-xs mt-1 mb-4 max-w-xs mx-auto" style={{ color: 'var(--muted)' }}>
                        Crie jornadas para organizar horários e turnos da equipe. Defina entradas, saídas e intervalos.
                    </p>
                    <Button onClick={() => setShowForm(true)} className="btn-gold text-sm" data-testid="empty-btn-nova-jornada">
                        <Plus size={14} className="mr-1" /> Criar Primeira Jornada
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {jornadas.map((j) => {
                        const hours = calcHours(j);
                        const saida = j.saida || j['saída'] || '17:00';
                        return (
                            <div key={j.jornada_id || j.nome} className="rounded-xl p-4 transition-all hover:scale-[1.005]" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`jornada-${j.jornada_id || j.nome}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)' }}>
                                            <CalendarDays size={16} style={{ color: 'var(--sv-gold-primary)' }} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{j.nome}</p>
                                            <p className="text-[10px]" style={{ color: 'var(--muted)' }}>{hours}h/dia - {(j.dias || []).length} dias/semana</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        {(j.dias || []).map(d => (
                                            <span key={d} className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)', color: 'var(--sv-gold-primary)' }}>{diasLabels[d] || d}</span>
                                        ))}
                                    </div>
                                </div>
                                {/* Visual Timeline */}
                                <div className="relative flex items-center gap-0 h-8 rounded-lg overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                                    <TimeBlock icon={Sun} label={j.entrada} color="#F5B544" title="Entrada" />
                                    <div className="flex-1 h-full flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)' }}>
                                        <span className="text-[9px] font-medium" style={{ color: 'var(--sv-gold-primary)' }}>Trabalho</span>
                                    </div>
                                    <TimeBlock icon={Coffee} label={j.intervalo_inicio} color="#F59E0B" title="Intervalo" />
                                    <div className="h-full w-12 flex items-center justify-center" style={{ background: 'rgba(245,158,11,0.1)' }}>
                                        <span className="text-[8px]" style={{ color: '#F59E0B' }}>Pausa</span>
                                    </div>
                                    <TimeBlock icon={Coffee} label={j.intervalo_fim} color="#F59E0B" title="Retorno" />
                                    <div className="flex-1 h-full flex items-center justify-center" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)' }}>
                                        <span className="text-[9px] font-medium" style={{ color: 'var(--sv-gold-primary)' }}>Trabalho</span>
                                    </div>
                                    <TimeBlock icon={Moon} label={saida} color="#6366F1" title="Saída" />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Upgrade CTA */}
            <div className="rounded-xl p-4" style={{ background: 'rgba(var(--sv-accent-rgb), 0.04)', border: '1px solid rgba(var(--sv-accent-rgb), 0.1)' }}>
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold" style={{ color: 'var(--sv-gold-primary)' }}>Escalas avançadas</p>
                        <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>Turnos rotativos, cobertura automática e notificações com planos superiores.</p>
                    </div>
                    <Link to="/planos" className="text-[10px] font-medium px-3 py-1.5 rounded-lg transition-all hover:scale-105" style={{ background: 'rgba(var(--sv-accent-rgb), 0.15)', color: 'var(--sv-gold-primary)' }} data-testid="upgrade-escalas">
                        Ver planos <ArrowRight size={10} className="inline ml-0.5" />
                    </Link>
                </div>
            </div>
        </div>
    );
}

function TimeBlock({ icon: Icon, label, color, title }) {
    return (
        <div className="flex flex-col items-center justify-center px-2 h-full shrink-0" style={{ borderRight: '1px solid var(--border)' }} title={title}>
            <Icon size={10} style={{ color }} />
            <span className="text-[8px] font-medium mt-0.5" style={{ color }}>{label}</span>
        </div>
    );
}
